import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, effect, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '@hq-core/auth/auth.service';
import {
    ApiCollectionResponse,
    SearchState,
} from '@cia-front-end-apps/shared/api-interaction';
import { NavBadgeService } from '@hq-core/navigation/nav-badge.service';
import { routeParts } from '@hq-core/routes';
import { injectQuery, injectQueryClient, queryOptions, toPromise } from '@ngneat/query';
import type { QueryKey } from '@tanstack/query-core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, filter, map, take, tap } from 'rxjs/operators';
import { Brand } from './models/brand';
import { BrandProduct } from './models/brand-product';
import { BrandProductResource } from './resources/brand-product.resource';
import { BrandResource } from './resources/brand.resource';
import { UndoToastService } from './undo-toast/undo-toast.service';

export type BrandSearchScope = 'all' | 'parents';

type QueueSnapshot = Array<[QueryKey, ApiCollectionResponse<Brand> | undefined]>;

interface PendingRemovalAction {
    brandId: number;
    removeOptimistically: () => void;
}

@Injectable({
    providedIn: 'root',
})
export class BrandAssignmentService {
    private readonly query = injectQuery();
    private readonly queryClient = injectQueryClient();
    private readonly brandResource = inject(BrandResource);
    private readonly brandProductResource = inject(BrandProductResource);
    private readonly navBadgeService = inject(NavBadgeService);
    private readonly undoToastService = inject(UndoToastService);
    private readonly snackBar = inject(MatSnackBar);
    private readonly authService = inject(AuthService);

    private readonly queueCountSearchState = new SearchState({
        pageIndex: 0,
        pageSize: 1,
        sortBy: 'createdDate',
        sortDirection: 'desc',
    });

    private readonly _queueCount = signal(0);
    readonly queueCount = this._queueCount.asReadonly();
    private readonly _pendingRemovalId = signal<number | null>(null);
    readonly pendingRemovalId = this._pendingRemovalId.asReadonly();
    private pendingRemovalAction: PendingRemovalAction | null = null;
    private readonly optimisticQueueRemovals = new Map<number, Brand>();

    constructor() {
        this.authService.getLoginState().pipe(
            filter(isLoggedIn => isLoggedIn),
            take(1),
        ).subscribe(() => this.refreshQueueCount());

        effect(() => {
            this.navBadgeService.setBadge(`/${routeParts.brandAssignment}`, this.queueCount());
        });
    }

    getBrandQueueOptions(searchState: SearchState) {
        return queryOptions({
            queryKey: [
                'brandQueue',
                searchState.pageIndex,
                searchState.pageSize,
                searchState.search,
                searchState.sortBy,
                searchState.sortDirection,
            ],
            queryFn: () => this.brandResource.getAll(searchState).pipe(
                map(response => this.applyOptimisticQueueRemovals(response, searchState.search)),
                tap(response => {
                    if (!searchState.search) {
                        this.setQueueCount(response);
                    }
                }),
            ),
            staleTime: 0,
        });
    }

    getBrandQueue(searchState: SearchState) {
        return this.query(this.getBrandQueueOptions(searchState));
    }

    getSearchBrandsOptions(term: string, scope: BrandSearchScope) {
        return queryOptions({
            queryKey: ['brandSearch', scope, term],
            queryFn: () => scope === 'parents'
                ? this.brandResource.searchParentBrands(term)
                : this.brandResource.searchBrands(term),
            enabled: scope === 'parents' || !!term,
            staleTime: 30_000,
        });
    }

    searchBrands(term: string, scope: BrandSearchScope) {
        return this.query(this.getSearchBrandsOptions(term, scope));
    }

    getBrandOptions(brandId: string | null) {
        return queryOptions({
            queryKey: ['brand', brandId],
            queryFn: () => {
                if (brandId === null) {
                    return throwError(() => new Error('brandId is required'));
                }
                return this.fetchBrandData(brandId);
            },
            enabled: brandId !== null,
            staleTime: 0,
        });
    }

    getBrand(brandId: string | null) {
        return this.query(this.getBrandOptions(brandId));
    }

    prefetchBrand(brandId: string): void {
        const { queryKey, staleTime } = this.getBrandOptions(brandId);
        void this.queryClient.prefetchQuery({
            queryKey,
            staleTime,
            queryFn: async () => toPromise({ source: this.fetchBrandData(brandId) }),
        });
    }

    private fetchBrandData(brandId: string) {
        return forkJoin({
            brand: this.brandResource.get(brandId),
            products: this.brandProductResource.getAll(brandId).pipe(
                catchError((error: HttpErrorResponse) =>
                    error.status === 404 ? of([]) : throwError(() => error)),
            ),
        }).pipe(
            map(({ brand, products }) => new Brand({ ...brand, products })),
        );
    }

    refreshBrandQueues(): void {
        this.queryClient.invalidateQueries({ queryKey: ['brandQueue'] });
        this.refreshQueueCount();
    }

    createNewBrand(brand: Brand): void {
        this.enqueueRemoval(
            brand,
            `"${brand.brandName}" created as a new brand.`,
            () => this.brandResource.process([brand.brandId]),
        );
    }

    rollUpBrand(newBrand: Brand, parentBrand: Brand): void {
        this.enqueueRemoval(
            newBrand,
            `"${newBrand.brandName}" rolled up into "${parentBrand.brandName}".`,
            () => this.brandResource.rollUp([newBrand.brandId], parentBrand.brandId),
            () => void this.queryClient.invalidateQueries({ queryKey: ['brand', String(parentBrand.brandId)] }),
        );
    }

    reassignUpc(product: BrandProduct, sourceBrand: Brand, targetBrand: Brand): void {
        this.enqueueUpcRemoval(
            sourceBrand,
            product,
            `UPC ${product.upc} reassigned to "${targetBrand.brandName}".`,
            () => this.brandProductResource.patch(sourceBrand.brandId, targetBrand.brandId, [product.itemId]),
            () => void this.queryClient.invalidateQueries({ queryKey: ['brand', String(targetBrand.brandId)] }),
        );
    }

    markUpcAsNewBrand(product: BrandProduct, sourceBrand: Brand, brandName: string): void {
        this.enqueueUpcRemoval(
            sourceBrand,
            product,
            `UPC ${product.upc} removed — will be created as "${brandName}".`,
            () => this.brandResource.create(sourceBrand.brandId, brandName, [product.itemId]),
            () => this.refreshBrandQueues(),
        );
    }

    commitPendingRemoval(): void {
        const action = this.pendingRemovalAction;
        if (!action || this._pendingRemovalId() !== action.brandId) {
            return;
        }

        action.removeOptimistically();
        this._pendingRemovalId.set(null);
    }

    private enqueueRemoval(
        brand: Brand,
        message: string,
        apiCall: () => Observable<unknown>,
        onSuccess?: () => void,
    ): void {
        let snapshot: QueueSnapshot = [];
        let isRemoved = false;

        const action: PendingRemovalAction = {
            brandId: brand.brandId,
            removeOptimistically: () => {
                if (isRemoved) {
                    return;
                }
                this.optimisticQueueRemovals.set(brand.brandId, brand);
                void this.queryClient.cancelQueries({ queryKey: ['brandQueue'] });
                snapshot = this.removeBrandFromQueueCaches(brand);
                isRemoved = true;
            },
        };

        const restore = () => {
            if (!isRemoved) {
                return;
            }
            this.optimisticQueueRemovals.delete(brand.brandId);
            snapshot.forEach(([queryKey, data]) => {
                if (data) {
                    this.queryClient.setQueryData(queryKey, data);
                }
            });
            const unfilteredSnapshot = snapshot.find(([queryKey, data]) => !queryKey[3] && data);
            if (unfilteredSnapshot?.[1]) {
                this.setQueueCount(unfilteredSnapshot[1]);
            }
            isRemoved = false;
        };

        const clearAction = () => {
            if (this.pendingRemovalAction === action) {
                this.pendingRemovalAction = null;
            }
            if (this._pendingRemovalId() === brand.brandId) {
                this._pendingRemovalId.set(null);
            }
        };

        this.undoToastService.enqueue({
            message,
            commit: () => {
                action.removeOptimistically();
                clearAction();
                apiCall().subscribe({
                    next: () => {
                        this.refreshBrandQueues();
                        onSuccess?.();
                    },
                    error: error => {
                        restore();
                        this.showMutationError(brand, error);
                    },
                });
            },
            undo: () => {
                clearAction();
                restore();
            },
        });

        this.pendingRemovalAction = action;
        this._pendingRemovalId.set(brand.brandId);
    }

    private enqueueUpcRemoval(
        sourceBrand: Brand,
        product: BrandProduct,
        message: string,
        apiCall: () => Observable<unknown>,
        onSuccess?: () => void,
    ): void {
        const queryKey: QueryKey = ['brand', String(sourceBrand.brandId)];
        const snapshot = this.queryClient.getQueryData<Brand>(queryKey);
        let isRemoved = false;

        const removeOptimistically = () => {
            if (isRemoved) { return; }
            void this.queryClient.cancelQueries({ queryKey });
            this.queryClient.setQueryData<Brand>(queryKey, current => {
                if (!current) { return current; }
                return new Brand({
                    ...current,
                    products: current.products.filter(p => p.itemId !== product.itemId),
                    productCount: Math.max(0, current.productCount - 1),
                });
            });
            isRemoved = true;
        };

        const restore = () => {
            if (!isRemoved) { return; }
            this.queryClient.setQueryData(queryKey, snapshot);
            isRemoved = false;
        };

        removeOptimistically();

        this.undoToastService.enqueue({
            message,
            commit: () => {
                apiCall().subscribe({
                    next: () => {
                        void this.queryClient.invalidateQueries({ queryKey });
                        onSuccess?.();
                    },
                    error: error => {
                        restore();
                        this.showMutationError(sourceBrand, error);
                    },
                });
            },
            undo: restore,
        });
    }

    private showMutationError(brand: Brand, error: unknown): void {
        const detail = this.getErrorDetail(error);
        const message = detail
            ? `Failed to update "${brand.brandName}": ${detail}`
            : `Failed to update "${brand.brandName}". Please try again.`;

        this.snackBar.open(message, null, { duration: 7000 });
    }

    private getErrorDetail(error: unknown): string | null {
        if (!(error instanceof HttpErrorResponse)) {
            return null;
        }

        return this.getErrorBodyMessage(error.error);
    }

    private getErrorBodyMessage(errorBody: unknown): string | null {
        if (typeof errorBody === 'string') {
            return errorBody.trim() || null;
        }

        if (!errorBody || typeof errorBody !== 'object') {
            return null;
        }

        const body = errorBody as Record<string, unknown>;
        return this.getValidationErrorMessage(body.errors)
            ?? this.getStringMessage(body.message)
            ?? this.getStringMessage(body.detail)
            ?? this.getStringMessage(body.title);
    }

    private getValidationErrorMessage(errors: unknown): string | null {
        if (!errors || typeof errors !== 'object') {
            return null;
        }

        for (const value of Object.values(errors as Record<string, unknown>)) {
            const message = Array.isArray(value)
                ? value.find(item => typeof item === 'string' && item.trim())
                : value;

            const text = this.getStringMessage(message);
            if (text) {
                return text;
            }
        }

        return null;
    }

    private getStringMessage(value: unknown): string | null {
        return typeof value === 'string' && value.trim() ? value.trim() : null;
    }

    private removeBrandFromQueueCaches(brand: Brand): QueueSnapshot {
        const snapshots = this.queryClient.getQueriesData<ApiCollectionResponse<Brand>>({
            queryKey: ['brandQueue'],
        });

        snapshots.forEach(([queryKey, response]) => {
            if (!response) {
                return;
            }

            const search = String(queryKey[3] ?? '');
            const nextResponse = this.applyOptimisticQueueRemovals(response, search);
            this.queryClient.setQueryData<ApiCollectionResponse<Brand>>(queryKey, nextResponse);
        });

        const unfilteredSnapshot = snapshots.find(([queryKey, data]) => !queryKey[3] && data);
        if (unfilteredSnapshot?.[1]) {
            this.setQueueCount(this.applyOptimisticQueueRemovals(unfilteredSnapshot[1], ''));
        }

        return snapshots;
    }

    private applyOptimisticQueueRemovals(
        response: ApiCollectionResponse<Brand>,
        search = '',
    ): ApiCollectionResponse<Brand> {
        if (this.optimisticQueueRemovals.size === 0) {
            return response;
        }

        const searchTerm = search.toLowerCase();
        const removedBrands = Array.from(this.optimisticQueueRemovals.values());
        const suppressedIds = new Set(removedBrands.map(brand => brand.brandId));
        const items = response.items?.filter(item => !suppressedIds.has(item.brandId)) ?? [];
        const removedCount = removedBrands.filter(brand =>
            !searchTerm || brand.brandName.toLowerCase().includes(searchTerm),
        ).length;
        const itemCount = response.meta?.itemCount ?? response.items?.length ?? 0;

        return {
            ...response,
            items,
            meta: {
                ...response.meta,
                itemCount: Math.max(0, itemCount - removedCount),
            },
        };
    }

    private setQueueCount(response: ApiCollectionResponse<Brand>): void {
        this._queueCount.set(response.meta?.itemCount ?? response.items?.length ?? 0);
    }

    private refreshQueueCount(): void {
        void this.queryClient.fetchQuery(this.getBrandQueueOptions(this.queueCountSearchState));
    }
}
